import React from 'react'
import axios from 'axios'
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/20/solid'
import {
  add,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  parse,
  startOfWeek,
} from 'date-fns'
import ColorPickerPopupForCalendar from '../ColorPickerPopupForCalendar/ColorPickerPopupForCalendar'
import { baseUrl } from '../../assets/Animations'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const toLocalDate = (value) => {
  if (value instanceof Date) return value
  const dateText = String(value || '').slice(0, 10)
  const match = dateText.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return new Date(value)
}

const CalendarSidebarMiniMonth = ({
  currentMonth,
  setCurrentMonth,
  selectedDay,
  setSelectedDay,
  calendarDaysWithEvents,
  view,
  setView,
}) => {
  const firstDayOfCurrentMonth = parse(currentMonth, 'MMMM yyyy', new Date())
  const days = eachDayOfInterval({
    start: startOfWeek(firstDayOfCurrentMonth),
    end: endOfWeek(endOfMonth(firstDayOfCurrentMonth)),
  })
  const selectedDate = toLocalDate(selectedDay?.date)

  const selectDay = (day) => {
    const dateKey = format(day, 'yyyy-MM-dd')
    const isAlreadySelected = !Number.isNaN(selectedDate.getTime()) && isSameDay(day, selectedDate)
    if (isAlreadySelected && typeof setView === 'function') {
      setView(view === 'Day view' ? 'Month view' : 'Day view')
      return
    }
    const dayDetails = calendarDaysWithEvents?.[dateKey] || {}
    setSelectedDay({
      date: dateKey,
      events: dayDetails.events || [],
      availabilities: dayDetails.availabilities || [],
    })
    if (!isSameMonth(day, firstDayOfCurrentMonth)) {
      setCurrentMonth(format(day, 'MMMM yyyy'))
    }
  }

  const changeMonth = (numberOfMonths) => {
    setCurrentMonth(format(add(firstDayOfCurrentMonth, { months: numberOfMonths }), 'MMMM yyyy'))
  }

  return (
    <section className='calendar__mini-month' aria-label='Mini month calendar'>
      <div className='calendar__mini-month-header'>
        <strong>{currentMonth}</strong>
        <div>
          <button type='button' onClick={() => changeMonth(-1)} aria-label='Previous month'>
            <ChevronLeftIcon aria-hidden='true' />
          </button>
          <button type='button' onClick={() => changeMonth(1)} aria-label='Next month'>
            <ChevronRightIcon aria-hidden='true' />
          </button>
        </div>
      </div>
      <div className='calendar__mini-month-weekdays' aria-hidden='true'>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => <span key={`${day}-${index}`}>{day}</span>)}
      </div>
      <div className='calendar__mini-month-days'>
        {days.map((day) => {
          const isSelected = !Number.isNaN(selectedDate.getTime()) && isSameDay(day, selectedDate)
          return (
            <button
              type='button'
              key={format(day, 'yyyy-MM-dd')}
              onClick={() => selectDay(day)}
              aria-label={format(day, 'MMMM d, yyyy')}
              aria-pressed={isSelected}
              className={classNames(
                !isSameMonth(day, firstDayOfCurrentMonth) && 'is-outside-month',
                isToday(day) && 'is-today',
                isSelected && 'is-selected'
              )}
            >
              {format(day, 'd')}
            </button>
          )
        })}
      </div>
    </section>
  )
}

const TeacherListForCalendar = ({
  teachersToDisplayObject,
  setTeachersToDisplayObject,
  loadingTeachers,
  unselectAllTeachers,
  selectedTeacherIds = [],
  onSelectTeacher,
  onClearSelectedTeachers,
  showExTeachers = false,
  onToggleShowExTeachers,
  schoolCalendar,
  setSchoolCalendar,
  userData,
  currentMonth,
  setCurrentMonth,
  selectedDay,
  setSelectedDay,
  calendarDaysWithEvents,
  createNewCalendarEvent,
  view,
  setView,
}) => {
  const [openColorPicker, setOpenColorPicker] = React.useState(false)
  const [teacherListCollapsed, setTeacherListCollapsed] = React.useState(false)
  const [teacherSearch, setTeacherSearch] = React.useState('')
  const [schools, setSchools] = React.useState([])
  const isManager = userData.PermissionLevel === 'manager' && userData.IsAdmin

  const myToken = localStorage.getItem('token')
  const myHeader = {headers: { Authorization: `Bearer ${myToken}` }}

  React.useEffect(() => {
    axios.get(`${baseUrl}/schools`)
      .then((res) => {
        const schoolList = Array.isArray(res.data) ? res.data : []
        setSchools(schoolList)
        const defaultSchool = isManager
          ? schoolList.find((school) => school.schoolAbbreviation === String(userData.School || '').toUpperCase())
          : schoolList.find((school) => school.schoolAbbreviation === 'MMA') || schoolList[0]
        if (defaultSchool) setSchoolCalendar(defaultSchool)
      })
      .catch((err) => console.log(err))
  }, [isManager, setSchoolCalendar, userData.School])

  const chooseTheSchool = (id) => {
    const selectedSchool = schools.find((school) => String(school.id) === String(id))
    if (selectedSchool) setSchoolCalendar(selectedSchool)
  }

  const showColorPicker = (id) => {
    const teacherMenu = document.getElementById(`teacher-calendar-${id}`)
    if (teacherMenu) teacherMenu.style.opacity = 1
  }

  const hideColorPicker = (id) => {
    const teacherMenu = document.getElementById(`teacher-calendar-${id}`)
    if (teacherMenu) teacherMenu.style.opacity = 0
  }

  const toggleTeacher = (teacherName) => {
    const teacherId = teachersToDisplayObject?.[teacherName]?.TeacherID
    if (!teacherId || typeof onSelectTeacher !== 'function') return
    onSelectTeacher(teacherId)
  }

  const changeTeacherColor = (teacherName, color) => {
    axios.put(
      `${baseUrl}/changecalendarcolor/${teachersToDisplayObject[teacherName].TeacherID}`,
      { CalendarColor: color },
      myHeader
    )
      .then((res) => {
        teachersToDisplayObject[teacherName].color = res.data.CalendarColor
        setTeachersToDisplayObject({...teachersToDisplayObject})
        setOpenColorPicker(false)
      })
      .catch(() => {})
  }

  const selectedIds = selectedTeacherIds.map((id) => String(id))
  const normalizedSearch = teacherSearch.trim().toLowerCase()
  const visibleTeachers = Object.keys(teachersToDisplayObject).filter((teacherName) => {
    const teacher = teachersToDisplayObject[teacherName]
    return teacher.ShowTeacherInList && (!normalizedSearch || teacherName.toLowerCase().includes(normalizedSearch))
  })

  return (
    <aside className='calendar__custom-left-column' aria-label='Calendar sidebar'>
      <button className='calendar__create-event-button' type='button' onClick={() => createNewCalendarEvent()} disabled={!schoolCalendar}>
        <PlusIcon aria-hidden='true' />
        <span>Create</span>
      </button>

      <CalendarSidebarMiniMonth
        currentMonth={currentMonth}
        setCurrentMonth={setCurrentMonth}
        selectedDay={selectedDay}
        setSelectedDay={setSelectedDay}
        calendarDaysWithEvents={calendarDaysWithEvents}
        view={view}
        setView={setView}
      />

      <label className='calendar__teacher-search'>
        <MagnifyingGlassIcon aria-hidden='true' />
        <input
          type='search'
          value={teacherSearch}
          onChange={(event) => setTeacherSearch(event.target.value)}
          placeholder='Search teachers'
          aria-label='Search teachers'
        />
      </label>

      <section className='calendar__school-calendars'>
        <div className='calendar__school-heading'>
          {isManager ? (
            <strong>{schoolCalendar?.name || 'School'}</strong>
          ) : (
            <label>
              <span className='sr-only'>School calendar</span>
              <select
                value={schoolCalendar?.id || ''}
                onChange={(event) => chooseTheSchool(event.target.value)}
                aria-label='School calendar'
              >
                {schools.map((school) => (
                  <option key={school.id} value={school.id}>{school.name}</option>
                ))}
              </select>
            </label>
          )}
          <button
            type='button'
            className='calendar__teacher-list-toggle'
            onClick={() => setTeacherListCollapsed((collapsed) => !collapsed)}
            aria-label={teacherListCollapsed ? 'Show teacher calendars' : 'Hide teacher calendars'}
            aria-expanded={!teacherListCollapsed}
          >
            <ChevronDownIcon className={teacherListCollapsed ? 'is-collapsed' : ''} aria-hidden='true' />
          </button>
        </div>

        {!teacherListCollapsed && (
          <div className='calendar__custom-left-column__teacher-list' id='calendar__custom-left-column__teacher-list'>
            <div className='calendar__teacher-list-actions'>
              <button type='button' onClick={() => {
                if (typeof onClearSelectedTeachers === 'function') onClearSelectedTeachers()
                else unselectAllTeachers()
              }}>
                Clear selection
              </button>
              <button type='button' onClick={() => typeof onToggleShowExTeachers === 'function' && onToggleShowExTeachers()}>
                {showExTeachers ? 'Hide former teachers' : 'Show former teachers'}
              </button>
            </div>

            {loadingTeachers && <div className='calendar__custom-left-column__teacher-list__loading'>Getting teachers…</div>}
            {!loadingTeachers && visibleTeachers.length === 0 && (
              <div className='calendar__teacher-list-empty'>No teachers found</div>
            )}
            {visibleTeachers.map((teacherName) => {
              const teacher = teachersToDisplayObject[teacherName]
              const isSelected = selectedIds.includes(String(teacher.TeacherID))
              return (
                <div
                  key={teacher.TeacherID}
                  onMouseEnter={() => showColorPicker(teacher.TeacherID)}
                  onMouseLeave={() => hideColorPicker(teacher.TeacherID)}
                  className='calendar__teacher-row'
                >
                  {openColorPicker === teacher.TeacherID && (
                    <ColorPickerPopupForCalendar
                      changeTeacherColor={changeTeacherColor}
                      teach={teacherName}
                      teachObj={teacher}
                      setOpenColorPicker={setOpenColorPicker}
                    />
                  )}
                  <button
                    type='button'
                    className='calendar__teacher-select'
                    onClick={() => toggleTeacher(teacherName)}
                    aria-pressed={isSelected}
                    aria-label={`${isSelected ? 'Hide' : 'Show'} ${teacherName}'s calendar`}
                  >
                    <span className={classNames(
                      'calendar__teacher-checkbox',
                      isSelected ? (teacher.color?.bgColor500 || 'bg-gray-300') : 'bg-white',
                      teacher.color?.borderColor500
                    )} aria-hidden='true' />
                    <span className={classNames('calendar__teacher-name', teacher.Status === 'Left School' && 'is-former')}>
                      {teacherName}
                    </span>
                  </button>
                  <button
                    id={`teacher-calendar-${teacher.TeacherID}`}
                    type='button'
                    onClick={() => setOpenColorPicker(teacher.TeacherID)}
                    className='calendar__teacher-color-menu'
                    aria-label={`Change ${teacherName}'s calendar color`}
                  >
                    •••
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </section>
    </aside>
  )
}

export { CalendarSidebarMiniMonth }
export default TeacherListForCalendar
